import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { HeartIcon } from '@phosphor-icons/react';
import InitialsAvatar from './InitialsAvatar';
import { CollaboratorProfile } from '../../contexts/UserWatchlistDataContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { usePerformanceMonitor } from '../../hooks/usePerformanceMonitor';
import { useWatchlistInfo, UseWatchlistInfoOptions } from '../../hooks/useWatchlistInfo';
import { User } from 'firebase/auth';
import { font } from '../../utils/fontHelper';

interface WatchlistWidgetProps {
  // Core watchlist identification
  watchlistType: string;
  user?: User | null;
  targetUserId?: string;
  isOtherUserWatchlist?: boolean;

  // Visual presentation
  moviePosters?: string[];
  movieIds?: string[]; // For optimized poster loading
  image?: string;
  isLarge?: boolean;
  variant?: 'default' | 'compact' | 'detailed';

  // Interaction handlers
  onClick?: () => void;
  onLikesClick?: () => void;
  onLikeToggle?: () => void;
  onAuthorClick?: () => void;
  onCollaboratorClick?: (uid: string) => void;

  // Optional external data (when not using context)
  externalWatchlistData?: UseWatchlistInfoOptions['externalWatchlistData'];

  // Legacy props for backward compatibility (will be ignored)
  title?: string;
  movies?: number;
  gradient?: string;
  isPrivate?: boolean;
  author?: string;
  authorAvatar?: string;
  authorUid?: string;
  collaborators?: number;
  collaboratorProfiles?: CollaboratorProfile[];
  followerCount?: number;
  watchlistId?: string;
  isOwned?: boolean;
  isFollowing?: boolean;
  showFollowButton?: boolean;
  isUserLiked?: boolean;
  useNewDesign?: boolean;
  fixedHeight?: boolean;
}

const WatchlistWidget: React.FC<WatchlistWidgetProps> = React.memo(({
  // Core props
  watchlistType,
  user,
  targetUserId,
  isOtherUserWatchlist = false,

  // Visual props
  moviePosters = [],
  movieIds = [],
  isLarge = false,
  variant = 'default',
  useNewDesign = true,
  fixedHeight = false,

  // Handlers
  onClick,
  onLikesClick,
  onLikeToggle,
  onAuthorClick,
  onCollaboratorClick,

  // Optional external data
  externalWatchlistData,

  // Legacy props (ignored)
  ...legacyProps
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  usePerformanceMonitor('WatchlistWidget');

  // Use centralized watchlist info hook
  const watchlistInfo = useWatchlistInfo({
    watchlistType,
    user,
    targetUserId,
    isOtherUserWatchlist,
    externalWatchlistData
  });

  const { getUserDisplayName, getUserAvatar } = useUserProfileCache();

  const validPosters = moviePosters.length > 0 ? moviePosters.slice(0, 3) : [];

  // Generate deterministic gradient based on watchlist ID/Type
  const getGradientColors = () => {
    const seed = watchlistType || 'default';
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }

    const options = [
      // Blue/Teal (Watched style)
      { start: isDark ? 'rgba(20, 55, 59, 0.95)' : 'rgba(38, 79, 165, 0.98)', end: 'rgba(38, 79, 165, 0)' },
      // Pink/Purple (Want to watch style)
      { start: isDark ? 'rgba(91, 28, 47, 0.95)' : 'rgba(126, 34, 162, 0.98)', end: 'rgba(126, 34, 162, 0)' },
      // Green/Forest
      { start: isDark ? 'rgba(25, 50, 25, 0.95)' : 'rgba(40, 100, 60, 0.98)', end: 'rgba(40, 100, 60, 0)' },
      // Indigo/Slate
      { start: isDark ? 'rgba(40, 40, 60, 0.95)' : 'rgba(70, 70, 120, 0.98)', end: 'rgba(70, 70, 120, 0)' },
      // Deep Orange/Brown
      { start: isDark ? 'rgba(60, 30, 10, 0.95)' : 'rgba(160, 80, 40, 0.98)', end: 'rgba(160, 80, 40, 0)' }
    ];

    const index = Math.abs(hash) % options.length;
    return options[index];
  };

  const gradientColors = getGradientColors();

  return (
    <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          position: 'relative',
          width: '100%',
          borderRadius: '16px',
          overflow: 'hidden', // Primary clipping
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          minHeight: fixedHeight ? '160px' : 'auto', // Dynamic height based on content
          height: fixedHeight ? '160px' : 'auto',
          boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
          bgcolor: isDark ? '#121212' : '#f5f5f5',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          },
        }}
      >
        {/* Inner container for proper border and clipping on iOS */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 'inherit',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden', // Secondary clipping layer
          isolation: 'isolate',
          display: 'flex',
          alignItems: 'flex-end',
          // Fix for iOS Safari border-radius clipping
          maskImage: 'radial-gradient(white, black)',
          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
          WebkitBackfaceVisibility: 'hidden',
          MozBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          transform: 'translate3d(0,0,0)',
        }}>
          {/* Background Layer - Poster Grid */}
          <Box sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            display: 'flex',
            backgroundColor: isDark ? '#121212' : '#f5f5f5',
            scale: 1.2,
          }}>
            {validPosters.length > 0 ? (
              validPosters.map((poster, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1, // Equal width, no gaps
                    height: '100%',
                    backgroundImage: `url(${poster})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(0px) brightness(0.9)', // Sharp image as requested
                  }}
                />
              ))
            ) : (
              <Box sx={{ width: '100%', height: '100%', bgcolor: gradientColors.start }} />
            )}
          </Box>

          {/* Gradient Overlay: Bottom-Left (Color) -> Top-Right (Transparent) */}
          <Box sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background: `linear-gradient(45deg, ${gradientColors.start} 40%, ${gradientColors.end} 100%)`,
          }} />

          {/* Content Layer */}
          <Box sx={{
            position: 'relative',
            zIndex: 2,
            p: 2,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}>
            {/* Title */}
            <Typography sx={{
              fontFamily: font(),
              color: 'white',
              fontWeight: 500,
              fontSize: '1rem',
              lineHeight: 1.3,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {watchlistInfo.title}
            </Typography>

            {/* Metadata Row */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>

              {/* Author */}
              {watchlistInfo.creatorName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.9)' }}>
                  <InitialsAvatar
                    src={watchlistInfo.creatorUid ? (getUserAvatar(watchlistInfo.creatorUid) || watchlistInfo.creatorAvatar) : watchlistInfo.creatorAvatar}
                    name={watchlistInfo.creatorUid ? getUserDisplayName(watchlistInfo.creatorUid, watchlistInfo.creatorName) : watchlistInfo.creatorName}
                    size={16}
                    sx={{ border: '1px solid rgba(255,255,255,0.5)' }}
                  />
                  <Typography fontFamily="Inter" fontSize="0.75rem" fontWeight={500} noWrap>
                    {watchlistInfo.isCollaborative
                      ? `${watchlistInfo.creatorUid ? getUserDisplayName(watchlistInfo.creatorUid, watchlistInfo.creatorName) : watchlistInfo.creatorName} + ${watchlistInfo.collaboratorsCount}`
                      : (watchlistInfo.creatorUid ? getUserDisplayName(watchlistInfo.creatorUid, watchlistInfo.creatorName) : watchlistInfo.creatorName)
                    }
                  </Typography>
                </Box>
              )}

              {/* Stats & Privacy */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                <Typography fontFamily="Inter" fontSize="inherit" fontWeight={500}>
                  {watchlistInfo.movieCount === 0 ? 'No movies' :
                    watchlistInfo.movieCount === 1 ? '1 movie' :
                      `${watchlistInfo.movieCount} movies`}
                </Typography>

                {watchlistInfo.showPrivacyIndicator && (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.6 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {watchlistInfo.isPrivate ? (
                        <Lock sx={{ fontSize: 14, color: 'inherit' }} />
                      ) : watchlistInfo.isUserLiked ? (
                        <HeartIcon size={14} color="#FF6B9D" weight="fill" />
                      ) : (
                        <HeartIcon size={14} weight="regular" />
                      )}
                      <Typography fontFamily="Inter" fontSize="inherit" fontWeight={500}>{watchlistInfo.displayPrivacyText}</Typography>
                    </Box>
                  </>
                )}
              </Box>

            </Box>
          </Box>
        </Box>
      </Paper>
    );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.useNewDesign === nextProps.useNewDesign &&
    prevProps.watchlistType === nextProps.watchlistType &&
    prevProps.user?.uid === nextProps.user?.uid &&
    prevProps.targetUserId === nextProps.targetUserId &&
    prevProps.isOtherUserWatchlist === nextProps.isOtherUserWatchlist &&
    prevProps.variant === nextProps.variant &&
    prevProps.fixedHeight === nextProps.fixedHeight &&
    prevProps.onAuthorClick === nextProps.onAuthorClick &&
    prevProps.onCollaboratorClick === nextProps.onCollaboratorClick &&
    JSON.stringify(prevProps.moviePosters) === JSON.stringify(nextProps.moviePosters) &&
    JSON.stringify(prevProps.externalWatchlistData) === JSON.stringify(nextProps.externalWatchlistData)
  );
});

export default WatchlistWidget; 